'use client';

import { useTranslations } from 'next-intl';
import { createContext, useCallback, useContext, useState } from 'react';

import { ResponsiveModal } from '@ui/components/features/selection/responsiveModal';
import { Button } from '@ui/components/ui/button';
import { type RateBookingData, RateBookingDrawer } from '@website/components/RateBookingDrawer';
import { useCurrentUser } from '@website/hooks/useCurrentUser';
import { getReviewsByActivityId } from '@website/services/reviews.service';

interface OpenRateBookingOptions {
  skipCheck?: boolean;
}

interface RateBookingContextValue {
  openRateBooking: (data: RateBookingData, options?: OpenRateBookingOptions) => void;
  closeRateBooking: () => void;
  isOpen: boolean;
}

const RateBookingContext = createContext<RateBookingContextValue | null>(null);

export function useRateBooking(): RateBookingContextValue {
  const ctx = useContext(RateBookingContext);
  if (!ctx) {
    throw new Error('useRateBooking must be used within a RateBookingProvider');
  }
  return ctx;
}

export function RateBookingProvider({ children }: { children: React.ReactNode }) {
  const t = useTranslations('RateBooking');
  const { data: user } = useCurrentUser();
  const [isOpen, setIsOpen] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [bookingData, setBookingData] = useState<RateBookingData | null>(null);

  const openRateBooking = useCallback(
    async (data: RateBookingData, options?: OpenRateBookingOptions) => {
      if (!options?.skipCheck && user?.id && data.activityId) {
        try {
          const result = await getReviewsByActivityId(data.activityId, { limit: 100 });
          if (result.reviews.some((r) => r.userId === user.id)) {
            setBookingData(data);
            setAlreadySubmitted(true);
            setIsOpen(true);
            return;
          }
        } catch {
          // If check fails, proceed with normal flow
        }
      }
      setBookingData(data);
      setAlreadySubmitted(false);
      setIsOpen(true);
    },
    [user]
  );

  const closeRateBooking = useCallback(() => {
    setIsOpen(false);
    setAlreadySubmitted(false);
    setBookingData(null);
  }, []);

  return (
    <RateBookingContext.Provider value={{ openRateBooking, closeRateBooking, isOpen }}>
      {children}
      {alreadySubmitted ? (
        <AlreadySubmittedDialog isOpen={isOpen} onClose={closeRateBooking} t={t} />
      ) : (
        <RateBookingDrawer isOpen={isOpen} onClose={closeRateBooking} bookingData={bookingData} />
      )}
    </RateBookingContext.Provider>
  );
}

function AlreadySubmittedDialog({
  isOpen,
  onClose,
  t
}: {
  isOpen: boolean;
  onClose: () => void;
  t: ReturnType<typeof useTranslations<'RateBooking'>>;
}) {
  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('alreadySubmittedTitle')}
      showCloseButton
      drawerClassName="p-0 rounded-t-xl"
      dialogClassName="sm:max-w-sm p-0"
    >
      <div className="flex flex-col gap-6 items-center px-5 pb-6 pt-4 md:px-8 md:py-8">
        <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-green-100 flex items-center justify-center">
          <svg
            className="w-8 h-8 md:w-10 md:h-10 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <div className="text-center">
          <h3 className="text-xl md:text-2xl font-semibold text-neutral-800">{t('alreadySubmittedTitle')}</h3>
          <p className="text-sm md:text-base text-neutral-500 mt-2">{t('alreadySubmittedMessage')}</p>
        </div>

        <Button onClick={onClose} className="w-full md:max-w-xs">
          {t('done')}
        </Button>
      </div>
    </ResponsiveModal>
  );
}
